import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import FeeItem from '../../../../components/FeeItem/FeeItem';
import FeePaymentMethod from '../../../../components/FeePaymentMethod';
import Icon, { Icons } from '../../../../components/Icon/Icon';
import ListItem from '../../../../components/ListItem';
import PageHeader from '../../../../components/PageHeader';
import ViewportAttachedButton from '../../../../components/ViewportAttachedButton';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import RequestService from '../../../../services/RequestService';
import TypesService from '../../../../services/TypesService';
import { receiveSlice, topUpCardSlice } from '../../../../store/slices';
import SendAmountInput from '../../../Send/components/SendAmount/components/SendAmountInput/SendAmountInput';

import './TopUpCardAmount.scss';

const { routes } = AppConfig;

const CurrenciesById = TypesService.CurrenciesById;

const TRX_ID = 2;
const USDT_TRC20_ID = 3;
const CREDIT_CURRENCY_ID = 8;

const commissionTypesByCurrencyId = {
	[TRX_ID]: 0,
	[CREDIT_CURRENCY_ID]: 1,
	[USDT_TRC20_ID]: 2,
};
const debouncedRequestTimeout = 1000;

const TopUpCardAmount = () => {
	const [loading, setLoading] = useState(false);
	const [card, setCard] = useState();
	const [asset, setAsset] = useState();
	const [sourceAccount, setSourceAccount] = useState();

	const availableCurrencies = useSelector(store => store.app.availableCurrencies);
	const cardsList = useSelector(state => state.card.cardsList);
	const tariffData = useSelector(store => store.app.tariffData);
	const amount = useSelector(state => state.topUpCard.amount);
	const feeData = useSelector(store => store.topUpCard.feeData);
	const isMaxAmountSetted = useSelector(store => store.topUpCard.isMaxAmountSetted);
	const withdrawalId = useSelector(store => store.topUpCard.withdrawalId);
	const walletAddresses = useSelector(store => store.topUpCard.walletAddresses);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const cardAddress = card?.Addresses?.find(address => address.CurrencyId === asset?.Id);
	const anotherCardsAddresses = cardsList
		.map(cardItem => {
			const address = cardItem?.Addresses?.find(address => address.CurrencyId === asset?.Id);
			if (cardItem.Id !== card?.Id && address) return address;
		})
		.filter(Boolean);
	const allSourceAddresses = [...walletAddresses, ...anotherCardsAddresses];

	const currencyData = CurrenciesById[asset?.Id];

	const feeMethodSelecting = currencyData?.FeeType === 'method_selecting';
	const feeCalculateInBack = currencyData?.FeeType === 'back_calculating';
	const feeConfiguratingInFront = currencyData?.FeeType === 'front_configurating';

	const billingFee = tariffData.maxTariff || 0;
	const feeByCurrencyIdForUSDT = {
		[USDT_TRC20_ID]: billingFee * 2,
		[CREDIT_CURRENCY_ID]: billingFee,
		[TRX_ID]: 18,
	};

	useEffect(() => {
		const cardId = Number(searchParams.get('cardId'));
		const card = cardsList.find(card => card.Id === cardId);
		setCard(card);

		const assetId = Number(searchParams.get('assetId'));
		const asset = availableCurrencies.find(currency => currency.Id === assetId);
		setAsset(asset);

		const addressId = Number(searchParams.get('addressId'));
		const sourceAccount = allSourceAddresses?.find(address => address.AddressId === addressId);
		setSourceAccount(sourceAccount);

		if (withdrawalId) {
			RequestService.WithdrawalCancelPrepare([withdrawalId]);
			dispatch(topUpCardSlice.actions.setWithdrawalId());
		} else {
			dispatch(topUpCardSlice.actions.setAmount());
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const goToReceive = () => {
		dispatch(receiveSlice.actions.setAccount(cardAddress));
		dispatch(receiveSlice.actions.setAsset(asset));
		dispatch(receiveSlice.actions.setIsCardTopUp(true));
		navigate(routes.receiveQR);
	};

	const calculateCommission = async (currencyId, sourceAddress, destinationAddress, amount, feeData) => {
		if (!amount) {
			dispatch(topUpCardSlice.actions.setFeeData({ ...feeData, Loading: false }));
			return;
		}

		try {
			const response = await RequestService.CalculateCommission(
				[sourceAddress],
				destinationAddress,
				currencyId,
				amount
			);

			if (!response) throw Error;

			if (response.Status === 'Success') {
				const comission = response?.Commission || 0;
				const fee = feeData.FeeRedacted ? feeData.Fee : comission;

				dispatch(topUpCardSlice.actions.setFeeData({ ...feeData, Fee: fee, DefaultFee: comission, Loading: false }));
			} else {
				throw response;
			}
		} catch (error) {
			console.log(error);
			dispatch(
				topUpCardSlice.actions.setFeeData({
					...feeData,
					Fee: feeConfiguratingInFront ? feeData.Fee : 0,
					DefaultFee: 0,
					Loading: false,
				})
			);
		}
	};

	const debouncedCalculateCommission = useRef(HelperService.debounce(calculateCommission, debouncedRequestTimeout));

	const prepareWithdrawal = async () => {
		const commissionType =
			feeMethodSelecting || feeConfiguratingInFront ? commissionTypesByCurrencyId[feeData.CurrencyId] : 0;
		try {
			setLoading(true);
			const numberAmount = Number(isMaxAmountSetted ? sourceAccount.Amount : amount);
			const topUpCardAmount = feeConfiguratingInFront && !isMaxAmountSetted ? numberAmount + feeData.Fee : numberAmount;

			const response = await RequestService.WithdrawalPrepare(
				sourceAccount.Address,
				cardAddress?.Address,
				asset.Symbol,
				topUpCardAmount,
				'',
				commissionType,
				feeConfiguratingInFront ? feeData.Fee : null
			);

			if (!response) return;
			if (response.Status !== 'Success') {
				throw Error();
			} else {
				dispatch(topUpCardSlice.actions.setWithdrawalId(response.WithdrawalId));
				navigate({
					pathname: routes.topUpCardConfirm,
					search: createSearchParams({
						cardId: card.Id,
						assetId: asset.Id,
						addressId: sourceAccount.AddressId,
					}).toString(),
				});
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const numericAmount = Number(amount);
	const isBalanceEnoughForAmount = numericAmount <= sourceAccount?.Amount;
	const feeAmountForValidate = feeData.Loading && !feeData.FeeRedacted ? 0 : feeData.Fee;
	const needAmountCoverFee =
		asset?.Id === feeData.CurrencyId &&
		!(isMaxAmountSetted && ((feeCalculateInBack && !currencyData?.Standart) || feeConfiguratingInFront));
	const isBalanceEnoughForAmountAndFee = needAmountCoverFee
		? numericAmount + feeAmountForValidate <= sourceAccount?.Amount
		: isBalanceEnoughForAmount;
	const isFeeAddressAmountEnoughForFee =
		feeAmountForValidate <= feeData.Amount || feeAmountForValidate <= feeData.GsBalance;
	const needwWaitForCalculateFee = !feeData.FeeRedacted && feeData.Loading;

	if (!card || !asset || !sourceAccount) return null;

	return (
		<div className='TopUpCardAmount'>
			<PageHeader header='Top up' collapsed />
			<div className='TopUpCardAmount__body'>
				<ListItem
					className='TopUpCardAmount__address FormBlock'
					icon={'QR_CODE'}
					iconSize='medium'
					title={t('Top up address')}
					description={cardAddress?.Address}
					trailing={[<Icon type={Icons.CHEVRON} width={6} height={12} />]}
					onSelect={goToReceive}
				/>
				<SendAmountInput
					asset={asset}
					account={sourceAccount}
					destinationAddress={cardAddress.Address}
					amount={amount}
					feeData={feeData}
					isMaxAmountSetted={isMaxAmountSetted}
					calculateCommission={debouncedCalculateCommission.current}
					setIsMaxAmountSetted={value => dispatch(topUpCardSlice.actions.setIsMaxAmountSetted(value))}
					setAmountToStore={amount => dispatch(topUpCardSlice.actions.setAmount(amount))}
					setFeeData={feeData => dispatch(topUpCardSlice.actions.setFeeData(feeData))}
					minAmountUsd={3}
				/>

				<div className='TopUpCardAmount__fee-header'>{t('Network fee')}</div>
				<FeeItem feeData={{ ...feeData, Loading: feeData.Loading && !feeData.FeeRedacted }} />
				{feeMethodSelecting && sourceAccount && (
					<FeePaymentMethod
						feeByCurrencyId={feeByCurrencyIdForUSDT}
						account={sourceAccount}
						feeData={feeData}
						setFeeData={feeData => dispatch(topUpCardSlice.actions.setFeeData(feeData))}
					/>
				)}
				<ViewportAttachedButton
					onClick={prepareWithdrawal}
					disabled={
						!numericAmount ||
						!isBalanceEnoughForAmountAndFee ||
						!isFeeAddressAmountEnoughForFee ||
						needwWaitForCalculateFee
					}
					loading={loading}
				>
					{t('Continue')}
				</ViewportAttachedButton>
			</div>
		</div>
	);
};

export default TopUpCardAmount;
